import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';

interface ConfirmScreenProps {
    email: string;
    onConfirmed: () => void;
    /** Optional context message (e.g. when reached by signing in unverified). */
    notice?: string;
}

/**
 * Sign-up email verification: the supplier enters the code emailed by Cognito.
 */
export default function ConfirmScreen({ email, onConfirmed, notice }: ConfirmScreenProps) {
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [resending, setResending] = useState(false);
    const [resent, setResent] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        if (!code.trim()) {
            setError('Enter the verification code sent to your email.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().confirmSignUp(email, code.trim());
            setConfirmed(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Confirmation failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setResent(false);
        setResending(true);
        try {
            await getAuthService().resendSignUpCode(email);
            setResent(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not resend the code. Please try again.');
        } finally {
            setResending(false);
        }
    };

    if (confirmed) {
        return (
            <AuthCard title="Email confirmed" subtitle="Your registration is complete.">
                <Column spacing="400">
                    <Alert type="success" size="small">
                        Your account is now awaiting administrator approval. You&apos;ll be able to
                        sign in once it&apos;s approved.
                    </Alert>
                    <Button type="primary" onClick={onConfirmed}>
                        Back to sign in
                    </Button>
                </Column>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Confirm your email" subtitle={`We sent a verification code to ${email}.`}>
            <Column spacing="400">
                {notice ? (
                    <Alert type="warning" size="small">
                        {notice}
                    </Alert>
                ) : null}
                <Input
                    id="confirm-code"
                    type="text"
                    label="Verification code"
                    value={code}
                    onChange={setCode}
                />
                {error ? (
                    <Alert type="error" size="small">
                        {error}
                    </Alert>
                ) : null}
                {resent ? (
                    <Alert type="success" size="small">
                        A new verification code has been sent to {email}.
                    </Alert>
                ) : null}
                <Button type="primary" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Confirming…' : 'Confirm'}
                </Button>
                <Button type="link" disabled={resending} onClick={handleResend}>
                    {resending ? 'Sending…' : "Didn't get a code? Resend"}
                </Button>
            </Column>
        </AuthCard>
    );
}

ConfirmScreen.defaultProps = {
    notice: undefined,
};
